import { FC, useEffect, useState } from 'react';
import { Button, ButtonGroup } from '@chakra-ui/react';
import { useSplunkLogger } from '@resolutionlife/ui';

import {
  Operation,
  TreatyDetailsFragment,
  TreatyUserDetailFragment,
  useUpdateUserEmailNotificationsMutation,
} from '../../../../graphql/generated';
import { byAlphabeticalLabel } from '../../../../utils/sorting/sorting';
import { convertCedantsToCedantNames } from '../../../../utils/treaty-utils/convert-cedants-to-cedant-names';
import { MultiCombobox, Option } from '../../../multi-combobox/multi-combobox';
import { Border, ReadOnlyView } from '../read-only-view';

type EditableEmailNotificationsProps = {
  treatyUser: TreatyUserDetailFragment;
  treaties: TreatyDetailsFragment[];
};

export const EditableEmailNotifications: FC<EditableEmailNotificationsProps> = ({
  treatyUser,
  treaties,
}) => {
  const [selectedEmailNotifs, setSelectedEmailNotifs] = useState<string[]>(
    treatyUser.emailNotifications
  );
  const [isEditable, setIsEditable] = useState<boolean>(false);

  const [showSaved, setShowSaved] = useState<boolean>(false);
  const [showFailed, setShowFailed] = useState<boolean>(false);
  const splunkLogger = useSplunkLogger();

  useEffect(() => {
    resetMessages();
  }, [treatyUser]);

  const [
    updateEmailNotifications,
    { loading },
  ] = useUpdateUserEmailNotificationsMutation({
    variables: {
      input: {
        userId: treatyUser.id,
        op: Operation.SET,
        cedants: selectedEmailNotifs,
      },
    },
    refetchQueries: ['GetTreatyUser'],
    awaitRefetchQueries: true,
  });

  const options = treaties
    ? treaties.map((treaty) => ({
        value: treaty.cedant,
        label: treaty.name,
      }))
    : [];

  const bySelected = (option: Option) =>
    selectedEmailNotifs.includes(option.value);
  const selectedOptions = options.filter(bySelected);

  const toggleEdit = () => setIsEditable(!isEditable);

  const resetMessages = () => {
    setShowSaved(false);
    setShowFailed(false);
  };

  const handleEmailNotificationsChange = (selectedOptions: Option[]) => {
    const emailNotifs = selectedOptions.map((option) => option.value);
    setSelectedEmailNotifs(emailNotifs);
    resetMessages();
  };

  const cancel = () => {
    setSelectedEmailNotifs(treatyUser.emailNotifications);
    resetMessages();
    toggleEdit();
  };

  const handleUpdateEmailNotifications = async () => {
    try {
      await updateEmailNotifications();
      setShowSaved(true);
    } catch {
      setShowFailed(true);
      splunkLogger.error('Email notification update failed', { eventCategory: 'user_management_error', userId: treatyUser.id });
    } finally {
      toggleEdit();
    }
  };

  const emailNotifsTags = convertCedantsToCedantNames(
    treatyUser.emailNotifications,
    treaties
  );
  const sortedEmailNotifsTags = [...emailNotifsTags];
  sortedEmailNotifsTags.sort((a, b) => a.localeCompare(b));

  if (!isEditable) {
    return (
      <ReadOnlyView
        label="Email Notifications"
        tags={sortedEmailNotifsTags}
        onEditClick={toggleEdit}
        showSaved={showSaved}
        showFailed={showFailed}
      />
    );
  }

  const sortedOptions = [...options];
  sortedOptions.sort(byAlphabeticalLabel);
  const sortedSelectedOptions = [...selectedOptions];
  sortedSelectedOptions.sort(byAlphabeticalLabel);

  return (
    <Border spacing={4}>
      <MultiCombobox
        label="Email Notifications"
        options={sortedOptions}
        selectedOptions={sortedSelectedOptions}
        onChange={handleEmailNotificationsChange}
        testId="email-notifs-multi-combobox"
        sx={{
          // prevents the label from moving when the view switches from read-only to editable
          label: {
            display: 'flex',
            alignItems: 'center',
            fontWeight: 600,
            minHeight: 10,
          },
        }}
      />
      <ButtonGroup colorScheme="blue">
        <Button
          onClick={handleUpdateEmailNotifications}
          isLoading={loading}
          data-testid="save-button"
        >
          Save
        </Button>
        <Button onClick={cancel} variant="ghost">
          Cancel
        </Button>
      </ButtonGroup>
    </Border>
  );
};
